import React from 'react';
import ErrorState from './ErrorState';

// ErrorBoundary compartido de VLS (auditoría de UI 2026-10-20, Lote 0,
// arreglo #10): si una pantalla se cae al dibujarse, en vez de la página en
// blanco o la plantilla en inglés con bg-blue-500 (grupos, visitas, crm,
// atv, fincas, cables, incidentes) sale un aviso en español con Reintentar y
// Volver al hub. El detalle técnico va a la consola, nunca a la pantalla.
//
// Va junto con ErrorState.jsx en la misma carpeta (sync-ui.sh --only=boundary
// copia los dos).
//
// ─── Migración (mecánica) ────────────────────────────────────────────────
//   ANTES:  import ErrorBoundary from './components/ErrorBoundary';   // plantilla
//   DESPUÉS: import ErrorBoundary from './components/ui/ErrorBoundary';
//     <ErrorBoundary>
//       <Routes>…</Routes>
//     </ErrorBoundary>
//   Para que al cambiar de ruta se limpie el error (sin recargar):
//     const { pathname } = useLocation();
//     <ErrorBoundary resetKeys={[pathname]}>…</ErrorBoundary>
//   Borrar después el ErrorBoundary.jsx propio de la app.
//
// Props:
//   children    lo que protege.
//   titulo      'Algo falló en esta pantalla' por defecto.
//   mensaje     texto bajo el título (por defecto dice que reintente y, si
//               se repite, avise a soporte).
//   hubUrl      destino de "Volver al hub" (https://vlscr.net por defecto;
//               null lo quita, para apps fuera del hub).
//   resetKeys   arreglo: si algún valor cambia, el error se limpia solo.
//   onReset     se llama al tocar Reintentar (por ejemplo, para volver a leer).
//   onError     (error, info) => void, además del console.error.
//   fallback    (error, reintentar) => nodo, para una pantalla propia.
//   pantallaCompleta  alto mínimo de pantalla (true por defecto); false
//               para envolver una sola sección.
//
// Si el error es de carga de un trozo de la app (pasa después de publicar
// una versión nueva con la pestaña abierta: "Failed to fetch dynamically
// imported module", "Loading chunk"), Reintentar recarga la página entera,
// que es lo único que lo arregla.

const RE_TROZO = /dynamically imported module|loading chunk|loading css chunk|importing a module script failed|error loading dynamically/i;

const C = {
  border: 'var(--color-border, #E7E5E4)',
  card: 'var(--color-card, #FFFFFF)',
  fg: 'var(--color-foreground, #1C1917)',
  bg: 'var(--color-background, #FAFAF9)',
};

function distintos(a = [], b = []) {
  return a.length !== b.length || a.some((v, i) => !Object.is(v, b[i]));
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.reintentar = this.reintentar.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error: error || new Error('error sin detalle') };
  }

  componentDidCatch(error, info) {
    if (typeof console !== 'undefined') console.error('[vls] ErrorBoundary:', error, info?.componentStack || '');
    this.props.onError?.(error, info);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && distintos(prevProps.resetKeys, this.props.resetKeys)) {
      this.setState({ error: null });
    }
  }

  reintentar() {
    const { error } = this.state;
    if (error && RE_TROZO.test(String(error.message || error)) && typeof window !== 'undefined') {
      window.location.reload();
      return;
    }
    this.props.onReset?.();
    this.setState({ error: null });
  }

  render() {
    const { error } = this.state;
    const {
      children, fallback, titulo = 'Algo falló en esta pantalla',
      mensaje, hubUrl = 'https://vlscr.net', pantallaCompleta = true,
    } = this.props;
    if (!error) return children;
    if (typeof fallback === 'function') return fallback(error, this.reintentar);

    const trozo = RE_TROZO.test(String(error.message || error));
    const texto = mensaje ?? (trozo
      ? 'Hay una versión nueva de la app. Recargue para seguir.'
      : 'Puede reintentar. Si vuelve a pasar, avise a soporte con lo que estaba haciendo.');
    return (
      <div
        data-vls-error-boundary=""
        className="flex flex-col items-center justify-center"
        style={{ minHeight: pantallaCompleta ? '100vh' : undefined, padding: '32px 16px', gap: 12, background: pantallaCompleta ? C.bg : undefined }}
      >
        <div style={{ width: '100%', maxWidth: 480 }}>
          <ErrorState
            title={trozo ? 'Hay una versión nueva' : titulo}
            message={texto}
            onRetry={this.reintentar}
            retryLabel={trozo ? 'Recargar' : 'Reintentar'}
          />
        </div>
        {hubUrl && (
          <a
            href={hubUrl}
            className="inline-flex items-center justify-center text-sm font-medium rounded-lg border hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            style={{ minHeight: 44, padding: '0 16px', borderColor: C.border, background: C.card, color: C.fg, textDecoration: 'none' }}
          >
            Volver al hub
          </a>
        )}
      </div>
    );
  }
}
